// services/notificationMessages.ts
//
// Duolingo-style notification copy — varied, punchy, never robotic.
// Each category has a pool. We rotate by hour-of-day so the same user
// doesn't get the same message twice in a row across days.

export interface NotifMessage {
  title: string
  body: string
}

function pick<T>(pool: T[]): T {
  const hour = new Date().getHours()
  return pool[hour % pool.length]
}

function truncate(text: string, max = 40): string {
  return text.length <= max ? text : text.slice(0, max - 1) + '…'
}

// ── Message pools ─────────────────────────────────────────────────────────────

const IDLE_SIGNALS: NotifMessage[] = [
  { title: "⚡ Your signals are waiting",       body: "You've got real work on the board. Time to lock in." },
  { title: "🎯 Ready when you are",             body: "Your highest-impact tasks haven't moved yet." },
  { title: "🔔 Signals need your attention",    body: "Don't let good work sit idle. Let's get one done." },
  { title: "⏰ Progress update: 0%",            body: "Nothing checked off yet today. One task to start." },
  { title: "💡 Your brain is warm, use it",     body: "Best focus window of the day. What's your first signal?" },
  { title: "🚀 Let's move one task forward",    body: "Even 25 minutes of deep focus compounds fast." },
  { title: "👀 Signals spotted",                body: "There are tasks classified as high-impact waiting on you." },
  { title: "🏁 Still at the start line?",       body: "Pick one signal and just begin. The momentum follows." },
  { title: "🧠 Deep work window open",          body: "Conditions are good. Lock in on something that matters." },
  { title: "⚡ Nothing running yet",             body: "Tap Play on a signal to start your first session today." },
]

const FOCUS_GAP: NotifMessage[] = [
  { title: "🔥 Long gap since your last focus",  body: "Two hours in and no deep work yet — what's blocking you?" },
  { title: "⏳ Clock is running",                body: "Morning's slipping by. Start one focus session before lunch." },
  { title: "📵 Distraction check",               body: "You haven't locked in yet today. What needs to happen first?" },
  { title: "🪃 Come back to your signals",       body: "The tab you had open isn't your highest-impact task." },
  { title: "⚡ 2 hours, no deep work",           body: "Your SNR is fragile right now. Time to build some signal." },
  { title: "🎯 One hour of focus = everything",  body: "You haven't started yet. One session changes the day." },
  { title: "📋 Still in setup mode?",            body: "Planning only counts when the work follows. Let's go." },
  { title: "🧱 Build your first block",          body: "A focus session before lunch sets the tone for the day." },
  { title: "⏰ Morning: almost over",             body: "Best concentration window closing. Pick a signal and lock in." },
  { title: "🚦 Red light day so far",            body: "Nothing focused yet. Even 20 min on a signal matters." },
]

const MIDDAY_SNR: NotifMessage[] = [
  { title: "📊 Midday SNR check",                body: "Your signal-to-noise ratio needs work. Back to what matters." },
  { title: "🔴 High noise detected",             body: "You've spent more time on noise than signals today." },
  { title: "⚖️ Balance check",                   body: "Noise is outweighing signal right now. Course-correct before 3pm." },
  { title: "📉 SNR below target",                body: "More noise than signal so far. One focused block fixes this." },
  { title: "🎯 Recalibrate",                     body: "Zoom out: what's the one thing that actually moves the needle today?" },
  { title: "⚡ Signal deficit",                   body: "You've been busy, but not on the right things. Pivot now." },
  { title: "🧭 Compass check",                   body: "Are the last 2 hours what you'd want to report tomorrow?" },
  { title: "🔁 Noise loop spotted",              body: "Responding, scrolling, reacting — none of it is signal. Pause." },
  { title: "💡 Half day left",                   body: "Afternoon is still yours. Make signal the priority from here." },
  { title: "📌 Midpoint audit",                  body: "Your top signal today: have you moved it forward at all?" },
]

const EOD_REVIEW: NotifMessage[] = [
  { title: "🌙 Day wrap incoming",               body: "Time to reflect. Your future self will thank you for 5 minutes." },
  { title: "📓 Review time",                     body: "What did you actually get done today? Let's find out." },
  { title: "🔍 End of day audit",                body: "Signals vs noise: how did today go? Open the review." },
  { title: "💡 Insights waiting",                body: "Your AI coach has thoughts on today. Check the Review tab." },
  { title: "🌇 Workday closing",                 body: "Reflection only takes 3 minutes. Make them count." },
  { title: "📊 Your daily SNR is ready",         body: "See how today broke down — signal, noise, and patterns." },
  { title: "🧠 Review = compound interest",       body: "Teams that review daily improve fastest. Your turn." },
  { title: "🎯 One question before you stop",    body: "What would you do differently tomorrow? Tap to reflect." },
  { title: "✅ Don't leave work unexamined",     body: "Close the loop on today. Open your daily review." },
  { title: "🏁 Finish line",                     body: "Mark what's done, log what's noise, set tomorrow up right." },
]

const BUDGET_EXHAUSTED: NotifMessage[] = [
  { title: "🔴 Noise budget hit",                body: "You've used up your noise allowance. Back to signal-only." },
  { title: "⛽ Noise tank empty",                body: "Budget exhausted. Everything from here should be Signal." },
  { title: "🚫 No more noise allowed",           body: "You've hit your limit. Lock the distractions out." },
  { title: "⚡ Budget blown — signal time",       body: "Noise budget is gone. Protect the rest of your day." },
  { title: "🔒 Noise locked out",                body: "You made it through your allowance. Now back to what matters." },
  { title: "⏱️ Budget used up",                  body: "Noise budget exhausted. Route everything to Signal from here." },
]

const ESTIMATE_ELAPSED: NotifMessage[] = [
  { title: "⏰ Estimate reached",    body: "You hit your estimate for: {task}. Still in it or time to move?" },
  { title: "⏳ Time check",          body: "Your {task} estimate just elapsed. Finishing up or extending?" },
  { title: "🔔 Over your estimate",  body: "Your planned time for \"{task}\" is up. Wrap it or log overtime?" },
  { title: "⚡ Estimate hit",         body: "You said {task} would take this long. How's it going?" },
  { title: "⏱️ Clock says done",     body: "Time's up on \"{task}\". Still the right priority?" },
  { title: "🎯 Estimate alarm",      body: "Your {task} window just closed. Mark done or keep the timer?" },
]

const FOCUS_CHECK_IN: NotifMessage[] = [
  { title: "👀 Still focused?",      body: "Just checking in on \"{task}\" — 45 min and counting." },
  { title: "⚡ Deep in it",           body: "45 minutes on \"{task}\". You're in a flow state. Keep going." },
  { title: "🔥 Locked in",           body: "Strong session on \"{task}\". How close to done are you?" },
  { title: "⏰ 45 min check",        body: "\"{task}\" — still the right focus? Or ready to wrap?" },
  { title: "🎯 Focus check",         body: "Over 45 min on \"{task}\". Log a break or keep the streak?" },
]

const SQUAD_ACCOUNTABILITY: NotifMessage[] = [
  { title: "🔥 {member} is locked in",         body: "{member} has been focusing for 1h+ on \"{task}\". Your move." },
  { title: "👀 {member} is in the zone",        body: "1h of deep work and counting. Don't let the squad outpace you." },
  { title: "⚡ Squad session live",             body: "{member} is deep in \"{task}\". Time to lock in alongside them." },
  { title: "🏃 {member} is running",           body: "They've been in session over an hour. You haven't started yet." },
  { title: "🎯 {member} is building signal",   body: "\"{task}\" — 1h in. Your squad is moving. Are you?" },
  { title: "💪 Accountability check",           body: "{member} is still focused. What's your excuse?" },
  { title: "🚀 {member} won't stop",            body: "Over an hour of deep work. The squad is watching the leaderboard." },
  { title: "⏱️ {member}: 60+ min focus",        body: "They're still going on \"{task}\". Time to stack your own session." },
]

// ── Public API ─────────────────────────────────────────────────────────────────

export const NotificationMessages = {
  idleSignals: (_count: number): NotifMessage =>
    pick(IDLE_SIGNALS),

  focusGap: (): NotifMessage =>
    pick(FOCUS_GAP),

  midDaySNR: (_snrPct: number): NotifMessage =>
    pick(MIDDAY_SNR),

  eodReview: (): NotifMessage =>
    pick(EOD_REVIEW),

  budgetExhausted: (): NotifMessage =>
    pick(BUDGET_EXHAUSTED),

  estimateElapsed: (taskText: string): NotifMessage => {
    const template = pick(ESTIMATE_ELAPSED)
    const short = truncate(taskText)
    return {
      title: template.title.replace('{task}', short),
      body:  template.body.replace(/{task}/g, short),
    }
  },

  focusCheckIn: (taskText: string): NotifMessage => {
    const template = pick(FOCUS_CHECK_IN)
    const short = truncate(taskText)
    return {
      title: template.title,
      body:  template.body.replace(/{task}/g, short),
    }
  },

  squadAccountability: (memberName: string, taskText: string): NotifMessage => {
    const template = pick(SQUAD_ACCOUNTABILITY)
    const shortTask = truncate(taskText)
    const shortName = truncate(memberName, 20)
    return {
      title: template.title.replace(/{member}/g, shortName),
      body:  template.body.replace(/{member}/g, shortName).replace(/{task}/g, shortTask),
    }
  },
}
